import { useEffect, useState } from "react";
import { Users, X, Ban, CircleCheck } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "../lib/api";
import { statusLabel, statusColor } from "../lib/orderStatus";

const euro = (n) => `€${Number(n || 0).toFixed(2).replace(".", ",")}`;
const subLabel = { active: "Box attivo", paused: "Box in pausa", canceled: "Box annullato" };

export const AdminCustomers = ({ onOpenOrder }) => {
  const [customers, setCustomers] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/admin/customers").then(({ data }) => setCustomers(data)).catch(() => setCustomers([]));
  useEffect(() => { load(); }, []);

  if (customers === null) return null;

  return (
    <section className="mt-2" data-testid="admin-customers">
      <div className="mb-4 flex items-center gap-2">
        <Users className="h-5 w-5 text-terra" strokeWidth={1.6} />
        <h2 className="font-serif text-2xl font-light text-ink">Clienti</h2>
        <span className="text-sm text-ink-soft" data-testid="admin-customer-count">({customers.length})</span>
      </div>
      {customers.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-ink/15 bg-panel p-8 text-center text-ink-soft" data-testid="admin-no-customers">Nessun cliente registrato.</p>
      ) : (
        <div className="space-y-2">
          {customers.map((c) => (
            <button key={c.user_id} onClick={() => setSelected(c)} data-testid={`customer-row-${c.user_id}`} className="flex w-full flex-wrap items-center gap-4 rounded-2xl border border-ink/10 bg-panel p-4 text-left transition-colors hover:border-terra/40">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-ink">
                  {c.name || "Senza nome"}
                  {c.suspended && <span className="ml-2 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-600" data-testid={`suspended-badge-${c.user_id}`}>Sospeso</span>}
                  {c.subscription_status && <span className="ml-2 rounded-full bg-sage/15 px-2.5 py-0.5 text-xs font-medium text-sage">{subLabel[c.subscription_status] || c.subscription_status}</span>}
                </p>
                <p className="truncate text-sm text-ink-soft">{c.email}</p>
              </div>
              <div className="text-right text-sm">
                <p className="font-medium text-ink" data-testid={`customer-spent-${c.user_id}`}>{euro(c.total_spent)}</p>
                <p className="text-xs text-ink-soft">{c.orders_count} ordini · credito {euro(c.credit)} · {c.points || 0} punti</p>
              </div>
            </button>
          ))}
        </div>
      )}
      {selected && <CustomerDrawer customer={selected} onClose={() => setSelected(null)} onSaved={() => { setSelected(null); load(); }} onOpenOrder={onOpenOrder} />}
    </section>
  );
};

const CustomerDrawer = ({ customer, onClose, onSaved, onOpenOrder }) => {
  const [form, setForm] = useState({
    name: customer.name || "",
    credit: customer.credit || 0,
    notes: customer.admin_notes || "",
    via: customer.address?.via || "",
    civico: customer.address?.civico || "",
    citta: customer.address?.citta || "",
    provincia: customer.address?.provincia || "",
    cap: customer.address?.cap || "",
    telefono: customer.address?.telefono || "",
  });
  const [orders, setOrders] = useState([]);
  const [saving, setSaving] = useState(false);
  const [suspending, setSuspending] = useState(false);

  useEffect(() => {
    api.get("/admin/orders").then(({ data }) => setOrders(data.filter((o) => o.user_id === customer.user_id))).catch(() => {});
  }, [customer.user_id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/customers/${customer.user_id}`, {
        name: form.name,
        credit: parseFloat(form.credit) || 0,
        admin_notes: form.notes,
        address: form.via ? { via: form.via, civico: form.civico, citta: form.citta, provincia: form.provincia, cap: form.cap, telefono: form.telefono } : null,
      });
      toast.success("Cliente aggiornato");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Salvataggio non riuscito"); } finally { setSaving(false); }
  };

  const toggleSuspend = async () => {
    const next = !customer.suspended;
    if (next && !window.confirm(`Sospendere ${customer.email}? Non potrà più accedere.`)) return;
    setSuspending(true);
    try {
      await api.put(`/admin/customers/${customer.user_id}`, { suspended: next });
      toast.success(next ? "Account sospeso" : "Account riattivato");
      onSaved();
    } catch { toast.error("Operazione non riuscita"); } finally { setSuspending(false); }
  };

  const inputCls = "mt-1.5 w-full rounded-xl border border-ink/15 bg-panel px-4 py-2.5 text-sm outline-none focus:border-terra";
  const addrCls = "w-full rounded-xl border border-ink/15 bg-panel px-4 py-2.5 text-sm outline-none focus:border-terra";

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-ink/40 backdrop-blur-sm" onClick={onClose}>
      <motion.div
        initial={{ x: "100%" }} animate={{ x: 0 }} transition={{ type: "spring", damping: 30, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="safe-pt safe-pb flex h-full w-full max-w-2xl flex-col overflow-y-auto bg-cream p-6 md:p-8"
        data-testid="customer-drawer"
      >
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-serif text-2xl text-ink">Cliente</h2>
          <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full hover:bg-ink/10"><X className="h-5 w-5" /></button>
        </div>
        <p className="mb-1 text-sm text-ink-soft">{customer.email}</p>
        <p className="mb-6 text-xs text-ink-soft">
          Iscritto il {customer.created_at ? new Date(customer.created_at).toLocaleDateString("it-IT") : "—"} ·
          {" "}{customer.orders_count} ordini · speso {euro(customer.total_spent)} · {subLabel[customer.subscription_status] || "Nessun box"} ·
          {" "}livello <strong className="text-sage">{customer.level || "Bronzo"}</strong> ({customer.points || 0} punti)
        </p>

        <label className="mb-4 block">
          <span className="text-xs uppercase tracking-overline text-ink-soft">Nome</span>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name" className={inputCls} />
        </label>
        <label className="mb-4 block">
          <span className="text-xs uppercase tracking-overline text-ink-soft">Credito (€)</span>
          <input type="number" step="0.01" min="0" value={form.credit} onChange={(e) => setForm({ ...form, credit: e.target.value })} data-testid="customer-credit" className={inputCls} />
        </label>

        <p className="mb-1.5 text-xs uppercase tracking-overline text-ink-soft">Indirizzo di spedizione</p>
        <div className="mb-6 space-y-2">
          <div className="flex gap-2">
            <input value={form.via} onChange={(e) => setForm({ ...form, via: e.target.value })} placeholder="Via" data-testid="customer-addr-via" className={addrCls} />
            <input value={form.civico} onChange={(e) => setForm({ ...form, civico: e.target.value })} placeholder="N. civico" data-testid="customer-addr-civico" className={`${addrCls} !w-24 shrink-0`} />
          </div>
          <div className="flex gap-2">
            <input value={form.citta} onChange={(e) => setForm({ ...form, citta: e.target.value })} placeholder="Comune di residenza" data-testid="customer-addr-citta" className={addrCls} />
            <input value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value })} placeholder="CAP" data-testid="customer-addr-cap" className={`${addrCls} !w-24 shrink-0`} />
          </div>
          <input value={form.provincia} onChange={(e) => setForm({ ...form, provincia: e.target.value })} placeholder="Provincia" data-testid="customer-addr-provincia" className={addrCls} />
          <input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="Telefono" data-testid="customer-addr-tel" className={addrCls} />
        </div>

        <p className="mb-2 text-xs uppercase tracking-overline text-ink-soft">I suoi ordini ({orders.length}) — clicca per aprire l'ordine</p>
        <div className="mb-6 space-y-2" data-testid="customer-orders">
          {orders.length === 0 && <p className="text-sm text-ink-soft">Nessun ordine.</p>}
          {orders.slice(0, 10).map((o) => (
            <button key={o.id} onClick={() => onOpenOrder?.(o.id)} className="flex w-full items-center justify-between rounded-xl border border-ink/10 bg-panel px-3 py-2 text-left text-sm transition-colors hover:border-terra/50" data-testid={`customer-order-${o.id}`}>
              <span className="text-ink">{new Date(o.created_at).toLocaleDateString("it-IT")} · {euro(o.total)}
                {(o.unread_admin || 0) > 0 && <span className="ml-2 rounded-full border border-terra/50 bg-terra/20 px-2 py-0.5 text-xs font-bold text-terra">msg {o.unread_admin}</span>}
              </span>
              <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusColor[o.status] || "bg-white/10 text-ink-soft border-white/20"}`}>{statusLabel[o.status] || o.status}</span>
            </button>
          ))}
        </div>

        {(customer.credit_history || []).length > 0 && (
          <>
            <p className="mb-2 text-xs uppercase tracking-overline text-ink-soft">Storico credito</p>
            <div className="mb-6 space-y-2" data-testid="customer-credit-history">
              {[...customer.credit_history].reverse().slice(0, 10).map((h, i) => (
                <div key={i} className="flex items-center justify-between rounded-xl border border-ink/10 bg-panel px-3 py-2 text-sm" data-testid={`credit-entry-${i}`}>
                  <span className="text-ink-soft">{new Date(h.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })} · {h.reason}</span>
                  <span className={`font-semibold ${h.amount >= 0 ? "text-sage" : "text-terra"}`}>{h.amount >= 0 ? "+" : ""}{euro(h.amount)}</span>
                </div>
              ))}
            </div>
          </>
        )}

        <label className="mb-6 block">
          <span className="text-xs uppercase tracking-overline text-ink-soft">Note admin (visibili solo a te)</span>
          <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="customer-notes" placeholder="Es. cliente abituale, preferenze, avvisi…" className="mt-1.5 w-full resize-none rounded-xl border border-ink/15 bg-panel px-4 py-2.5 text-sm outline-none focus:border-terra" />
        </label>

        <button onClick={save} disabled={saving} data-testid="save-customer-button" className="w-full rounded-full bg-ink py-3.5 text-sm font-semibold text-cream hover:bg-sage disabled:opacity-60">
          {saving ? "Salvataggio…" : "Salva modifiche"}
        </button>
        <button onClick={toggleSuspend} disabled={suspending} data-testid="suspend-customer-button" className={`mt-3 flex w-full items-center justify-center gap-2 rounded-full border py-3 text-sm font-semibold disabled:opacity-60 ${customer.suspended ? "border-sage/40 bg-sage/10 text-sage hover:bg-sage/20" : "border-red-300 bg-red-50 text-red-600 hover:bg-red-100"}`}>
          {customer.suspended ? <><CircleCheck className="h-4 w-4" /> Riattiva account</> : <><Ban className="h-4 w-4" /> Sospendi account</>}
        </button>
      </motion.div>
    </div>
  );
};
